import { RolesEnum } from '../models/enums/roles.enum';
import { UserProxy } from '../models/proxies/user.proxy';
import type { AttachmentUrl } from '../pages/main/chat/chat.component';
import { emailRegex, vimeoUrlRegex } from './constants';

export async function delay(milliseconds: number): Promise<void> {
  return await new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export function isUuid(value: string): boolean {
  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return UUID_REGEX.test(value);
}

export function getLeftPad(num: number): string {
  return num <= 9 ? `0${num}` : `${num}`;
}

export function noRef<T>(mock: T): T {
  if (['string', 'number'].includes(typeof mock)) return mock;

  return JSON.parse(JSON.stringify(mock));
}

export function isAdminUser(user?: UserProxy): boolean {
  return !!user && !!user.permissions && hasRole(user.permissions, RolesEnum.ADMIN);
}

export function isTeacherUser(user?: UserProxy): boolean {
  return (
    !!user && !!user.permissions && hasRole(user.permissions, RolesEnum.TEACHER)
  );
}

export function isNormalUser(user?: UserProxy): boolean {
  return (
    !!user &&
    !!user.permissions &&
    hasRole(user.permissions, RolesEnum.USER) &&
    !isAdminUser(user) && !isTeacherUser(user)
  );
}

export function hasRole(roles: string, targetRole: string): boolean {
  return !!roles && roles.split('|').some((role) => role === targetRole);
}

export function getErrorMessage(errorResponse?: any): string {
  if (!errorResponse) return 'Ocorreu um erro inesperado, por favor, tente novamente mais tarde.';

  const { status, error, message } = errorResponse;

  if (status === 503)
    return (
      error?.message ||
      'Ocorreu um erro inesperado, por favor, experimente atualizar a página e tentar novamente.'
    );

  if (status >= 500 && status <= 599)
    return 'Ocorreu um erro inesperado, por favor, experimente atualizar a página e tentar novamente.';

  if (!error) {
    if (Array.isArray(message)) return message[0];

    return (
      message ||
      'Ocorreu um erro inesperado, por favor, experimente atualizar a página e tentar novamente.'
    );
  }

  if (!Array.isArray(error.message)) {
    if (typeof error.message === 'string' && error.message.includes('Cannot'))
      return 'A rota especificada não foi encontrada, por favor, contate os administradores se o erro persistir.';

    return (
      error.message ||
      'Ocorreu um erro inesperado, por favor, experimente atualizar a página e tentar novamente.'
    );
  }

  if (error.message.every((message: any) => typeof message === 'string'))
    return error.message[0];

  // @ts-ignore
  return error.message
    .map(({ constraints }: any) => (constraints && Object.values(constraints)) || [])
    .reduce((acc: string, actual: string) => [...acc, ...actual] as string[])[0];
}

export function convertSecondsToHour(timeInSeconds: number): string {
  const hours = Math.floor(timeInSeconds / (60 * 60));
  let minutes = Math.floor(timeInSeconds / 60);
  let slackMinutes = Math.floor(minutes / 60);
  let restHours = timeInSeconds % (60 * 60);

  minutes = minutes - 60 * slackMinutes;
  restHours %= 60;
  const seconds = Math.ceil(restHours);

  return `${hours}:${minutes}:${seconds}`;
}

export function convertHourToSeconds(time: string): number {
  let currentTime = time.split(':'); // split it at the colons

  return +currentTime[0] * 60 * 60 + +currentTime[1] * 60 + +currentTime[2];
}

export function getFirstLetterOfFirstAndLastNames(userName: string): string[] {
  userName = userName.trim();
  const nameFirstLetter = userName[0].toUpperCase();

  const splitName = userName.split(' ');

  let lastnameFirstLetter = splitName[splitName.length - 1].toUpperCase();
  lastnameFirstLetter = [...lastnameFirstLetter][0];

  return [nameFirstLetter, lastnameFirstLetter];
}

export function getVimeoIdByUrl(url: string): string {
  const match = url.match(vimeoUrlRegex);

  if (match)
    return match[1].split('/')[1];

  throw new Error('Esse não é um link do Vimeo, tente novamente!');
}

export function shuffle<T>(array: Array<T>): Array<T> {
  if (!array)
    return [];

  let currentIndex: number = array.length;
  let randomIndex: number = 0;

  while (currentIndex > 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]
    ];
  }

  return array;
}

export function isValidEmail(email: string): boolean {
  if (!email)
    return false ;

  if (!emailRegex.test(email))
    return false;

  return true;
}

export async function downloadFile(url: string, fileName: string): Promise<void> {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Erro ao baixar arquivo: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);

    triggerDownload(downloadUrl, fileName);

    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    triggerDownload(url, fileName, true);
  }
}

function triggerDownload(url: string, fileName: string, newTab: boolean = false): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;

  if (newTab) {
    link.target = '_blank';
  }

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function getFileName(file: string | AttachmentUrl): string {
  if (!file) return '';

  const fileUrl = typeof file === 'string' ? file : file.url;

  try {
    const url = fileUrl.split('?')[0];
    const segments = url.split('/');
    return segments[segments.length - 1] || fileUrl;
  } catch {
    return fileUrl;
  }
}

export function getFileExtension(file: File | string | AttachmentUrl): string {
  const fileName = typeof file === 'string'
    ? file
    : (file as AttachmentUrl).url ?? (file as File).name;

  const cleanName = getFileName(fileName);
  const parts = cleanName.split('.');

  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

export function getMediaTypeFromMime(mimeType: string):
  | 'videos'
  | 'images'
  | 'lessons'
  | 'instances'
  | 'text'
  | 'pdf'
  | 'csv'
  | 'mp3'
  | null {
  if (!mimeType) return null;

  const normalized = mimeType.toLowerCase();

  if (normalized.startsWith('video/')) return 'videos';
  if (normalized.startsWith('image/')) return 'images';
  if (normalized === 'application/pdf') return 'pdf';
  if (
    normalized === 'text/csv' ||
    normalized === 'application/csv' ||
    normalized === 'application/vnd.ms-excel'
  )
    return 'csv';
  if (normalized === 'text/plain') return 'text';
  if (normalized === 'audio/mpeg' || normalized === 'audio/mp3') return 'mp3';

  return null;
}

